import { ArrowLeftOutlined, PictureOutlined, PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Empty, Form, Input, Modal, Skeleton, Tag, Typography, Upload, message } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { useCallback, useEffect, useState } from 'react'
import {
  createSiteEntry,
  listSiteEntries,
  siteAssetVariantUrl,
  uploadSiteAsset,
} from '../../api'
import type { SaasApiError } from '../../api'
import type { ContentEntry } from '../../types'

const { Title, Text, Paragraph } = Typography

const CONTENT_TYPE_COLLECTION = 'collection'
const CONTENT_TYPE_ITEM = 'item'

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

interface EntryFormValues {
  title: string
  slug: string
  description: string
  _lastAutoTitle?: string
}

/** Small shared modal for both "Create Collection" and "Create Item" —
 * only the label/content_type/extra fields differ, the Title/Slug/
 * Description/Image shape is identical (spec §8/§9). */
function EntryFormModal({
  open,
  title,
  onCancel,
  onSubmit,
  submitting,
}: {
  open: boolean
  title: string
  onCancel: () => void
  onSubmit: (values: EntryFormValues, file: File | null) => Promise<void>
  submitting: boolean
}) {
  const [form] = Form.useForm<EntryFormValues>()
  const [fileList, setFileList] = useState<UploadFile[]>([])

  useEffect(() => {
    if (open) {
      form.resetFields()
      setFileList([])
    }
  }, [open, form])

  const onTitleChange = (value: string) => {
    const currentSlug = form.getFieldValue('slug')
    const autoSlug = slugify(form.getFieldValue('_lastAutoTitle') || '')
    if (!currentSlug || currentSlug === autoSlug) {
      form.setFieldsValue({ slug: slugify(value), _lastAutoTitle: value })
    }
  }

  const handleOk = async () => {
    const values = await form.validateFields()
    const file = fileList[0]?.originFileObj ?? null
    await onSubmit(values, file)
  }

  return (
    <Modal title={title} open={open} onOk={handleOk} onCancel={onCancel} okText="Create" cancelText="Cancel" confirmLoading={submitting}>
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
          <Input placeholder="Chinese Art" onChange={(e) => onTitleChange(e.target.value)} />
        </Form.Item>
        <Form.Item
          name="slug"
          label="Slug"
          rules={[{ required: true, message: 'Slug is required' }, { pattern: /^[a-z0-9-]+$/, message: 'Lowercase letters, numbers and hyphens only' }]}
        >
          <Input placeholder="chinese-art" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} placeholder="Local Vistoera integration test." />
        </Form.Item>
        <Form.Item label="Image">
          <Upload
            listType="picture-card"
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: next }) => setFileList(next.slice(-1))}
            maxCount={1}
            accept="image/*"
          >
            {fileList.length === 0 && (
              <span>
                <PictureOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </span>
            )}
          </Upload>
        </Form.Item>
        <Form.Item name="_lastAutoTitle" hidden>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  )
}

/**
 * The Content tab — the first real place LiangCollection's Collection/
 * Object capability becomes visible/clickable inside Vistoera (spec
 * §8/§9). A Collection Website preset: Collections list on top; drilling
 * into one manages its Items. Every create goes through the real
 * UI -> API -> DB loop (`createSiteEntry`/`uploadSiteAsset`), never a
 * frontend-only mock (spec §19).
 */
export default function ContentTab({ workspaceId, siteId }: { workspaceId: string; siteId: string }) {
  const [entries, setEntries] = useState<ContentEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null)
  const [createCollectionOpen, setCreateCollectionOpen] = useState(false)
  const [createItemOpen, setCreateItemOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(() => {
    setError(null)
    listSiteEntries(workspaceId, siteId)
      .then((r) => setEntries(r.data))
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId])

  useEffect(load, [load])

  if (error) {
    return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  }
  if (entries === null) {
    return <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 16 }} />
  }

  const collections = entries.filter((e) => e.content_type === CONTENT_TYPE_COLLECTION)
  const items = entries.filter((e) => e.content_type === CONTENT_TYPE_ITEM)
  const activeCollection = activeCollectionId ? collections.find((c) => c.id === activeCollectionId) ?? null : null
  const itemsInActiveCollection = activeCollectionId
    ? items.filter((i) => i.fields.collection_id === activeCollectionId)
    : []

  const createEntry = async (
    contentType: string,
    values: EntryFormValues,
    file: File | null,
    extraFields: Record<string, unknown>,
  ) => {
    setSubmitting(true)
    try {
      let coverAssetId: string | null = null
      if (file) {
        const { data: asset } = await uploadSiteAsset(workspaceId, siteId, file)
        coverAssetId = asset.id
      }
      await createSiteEntry(workspaceId, siteId, {
        content_type: contentType,
        slug: values.slug.trim(),
        label: values.title.trim(),
        fields: { description: values.description?.trim() || '', ...extraFields },
        status: 'published',
        cover_asset_id: coverAssetId,
      })
      message.success(`"${values.title}" created`)
      load()
      return true
    } catch (err) {
      message.error((err as SaasApiError).message)
      return false
    } finally {
      setSubmitting(false)
    }
  }

  const onCreateCollection = async (values: EntryFormValues, file: File | null) => {
    const ok = await createEntry(CONTENT_TYPE_COLLECTION, values, file, {})
    if (ok) setCreateCollectionOpen(false)
  }

  const onCreateItem = async (values: EntryFormValues, file: File | null) => {
    if (!activeCollectionId) return
    const ok = await createEntry(CONTENT_TYPE_ITEM, values, file, { collection_id: activeCollectionId })
    if (ok) setCreateItemOpen(false)
  }

  const CoverImage = ({ assetId, alt }: { assetId: string | null; alt: string }) =>
    assetId ? (
      <img
        src={siteAssetVariantUrl(workspaceId, assetId, 'thumbnail')}
        alt={alt}
        style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block', background: '#f0f0f0' }}
      />
    ) : (
      <div
        style={{
          width: '100%',
          aspectRatio: '4/3',
          background: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#bbb',
        }}
      >
        <PictureOutlined style={{ fontSize: 28 }} />
      </div>
    )

  if (activeCollection) {
    return (
      <div style={{ marginTop: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => setActiveCollectionId(null)} style={{ marginBottom: 16 }}>
          Back to Collections
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {activeCollection.label}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {String(activeCollection.fields.description || '') || 'No description yet.'}
            </Paragraph>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateItemOpen(true)}>
            Create Item
          </Button>
        </div>
        {itemsInActiveCollection.length === 0 ? (
          <Empty description="No items in this collection yet" style={{ marginTop: 60 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateItemOpen(true)}>
              Create Item
            </Button>
          </Empty>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {itemsInActiveCollection.map((item) => (
              <Card key={item.id} cover={<CoverImage assetId={item.cover_asset_id} alt={item.label} />}>
                <Card.Meta
                  title={item.label}
                  description={String(item.fields.description || '')}
                />
                <Tag style={{ marginTop: 8 }} color={item.status === 'published' ? 'green' : 'default'}>
                  {item.status}
                </Tag>
              </Card>
            ))}
          </div>
        )}
        <EntryFormModal
          open={createItemOpen}
          title={`Create Item in "${activeCollection.label}"`}
          onCancel={() => setCreateItemOpen(false)}
          onSubmit={onCreateItem}
          submitting={submitting}
        />
      </div>
    )
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>
          Collections
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateCollectionOpen(true)}>
          Create Collection
        </Button>
      </div>
      {collections.length === 0 ? (
        <Empty description="No collections yet — create one to start adding items" style={{ marginTop: 60 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateCollectionOpen(true)}>
            Create Collection
          </Button>
        </Empty>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {collections.map((collection) => {
            const itemCount = items.filter((i) => i.fields.collection_id === collection.id).length
            return (
              <Card
                key={collection.id}
                hoverable
                cover={<CoverImage assetId={collection.cover_asset_id} alt={collection.label} />}
                onClick={() => setActiveCollectionId(collection.id)}
              >
                <Card.Meta title={collection.label} description={String(collection.fields.description || '')} />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{itemCount} item{itemCount === 1 ? '' : 's'}</Text>
                </div>
              </Card>
            )
          })}
        </div>
      )}
      <EntryFormModal
        open={createCollectionOpen}
        title="Create Collection"
        onCancel={() => setCreateCollectionOpen(false)}
        onSubmit={onCreateCollection}
        submitting={submitting}
      />
    </div>
  )
}
